import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Radio,
    RadioGroup,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { AuthContext } from "../allauth/auth/AuthContext";
import { getAuth, logout } from "../allauth/lib/allauth";
import ApiClient from "../config/ApiClient";

interface DigitalConsent {
    email: boolean;
    text: boolean;
    video_call: boolean;
}

type Segment = {
    type: 'markdown' | 'form';
    content?: string;
    formType?: 'digital_consent' | 'media_release' | 'emergency_contact' | 'signature';
};

export default function WaiverModal() {
    const { auth } = React.useContext<any>(AuthContext) || {};
    const theme = useTheme();

    const user = auth?.data?.user;
    const isAuthenticated = auth?.meta?.is_authenticated === true;
    const open = useMemo(() => Boolean(isAuthenticated && user && user.waiver_signed === false), [isAuthenticated, user]);

    const [markdownContent, setMarkdownContent] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [digitalConsent, setDigitalConsent] = useState<DigitalConsent>({
        email: false,
        text: false,
        video_call: false
    });
    const [mediaReleaseSigned, setMediaReleaseSigned] = useState<boolean>(false);
    const [emergencyContact, setEmergencyContact] = useState<string>("");
    const [signature, setSignature] = useState<string>("");

    // Sync form state with user data when available
    useEffect(() => {
        if (user) {
            setDigitalConsent({
                email: user.digital_consent?.email || false,
                text: user.digital_consent?.text || false,
                video_call: user.digital_consent?.video_call || false
            });
            setMediaReleaseSigned(user.media_release_signed || false);
            setEmergencyContact(user.emergency_contact || "");
        }
    }, [user]);

    // Load markdown content
    useEffect(() => {
        if (!open) return;

        const loadMarkdown = async () => {
            try {
                const response = await fetch('/oa-assets/waiver-consent-form.md');
                if (response.ok) {
                    const text = await response.text();
                    setMarkdownContent(text);
                } else {
                    console.error('Failed to load waiver form');
                }
            } catch (error) {
                console.error('Error loading waiver form:', error);
            } finally {
                setLoading(false);
            }
        };

        loadMarkdown();
    }, [open]);

    // Parse markdown and split into segments
    const segments = useMemo(() => {
        if (!markdownContent) return [];

        const segments: Segment[] = [];
        const formPlaceholders = [
            { marker: '[FORM:digital_consent]', type: 'digital_consent' as const },
            { marker: '[FORM:media_release]', type: 'media_release' as const },
            { marker: '[FORM:emergency_contact]', type: 'emergency_contact' as const },
            { marker: '[FORM:signature]', type: 'signature' as const }
        ];

        // Find all form placeholders and their positions
        const formPositions: Array<{ index: number; type: Segment['formType']; marker: string }> = [];
        formPlaceholders.forEach(({ marker, type }) => {
            let searchIndex = 0;
            while (true) {
                const index = markdownContent.indexOf(marker, searchIndex);
                if (index === -1) break;
                formPositions.push({ index, type, marker });
                searchIndex = index + marker.length;
            }
        });

        // Sort by position
        formPositions.sort((a, b) => a.index - b.index);

        // Split text at form positions
        let currentIndex = 0;
        formPositions.forEach(({ index, type, marker }) => {
            // Add markdown segment before form
            if (index > currentIndex) {
                const markdownSegment = markdownContent.substring(currentIndex, index);
                if (markdownSegment.trim()) {
                    segments.push({
                        type: 'markdown',
                        content: markdownSegment
                    });
                }
            }

            // Add form segment
            segments.push({
                type: 'form',
                formType: type
            });

            currentIndex = index + marker.length;
        });

        // Add remaining markdown
        if (currentIndex < markdownContent.length) {
            const remainingMarkdown = markdownContent.substring(currentIndex);
            if (remainingMarkdown.trim()) {
                segments.push({
                    type: 'markdown',
                    content: remainingMarkdown
                });
            }
        }

        return segments;
    }, [markdownContent]);

    const onAccept = useCallback(async () => {
        if (!user?.id) return;
        if (!signature.trim()) {
            // Signature is required
            return;
        }

        const payload: any = {
            waiver_signed: true,
            digital_consent: digitalConsent,
            media_release_signed: mediaReleaseSigned,
            emergency_contact: emergencyContact
        };

        const resp = await ApiClient.patch(`/api/users/${user.id}`, payload);
        if (resp.success) {
            try {
                await getAuth(); // refresh session; event updates AuthContext
            } catch (e) {
                // ignore, session refresh will retry on next tick
            }
        }
    }, [user, digitalConsent, mediaReleaseSigned, emergencyContact, signature]);

    const onLogout = useCallback(async () => {
        try {
            await logout();
            // The logout function will trigger an auth change event that updates the context
            // The modal will close automatically when user.waiver_signed becomes undefined (user is logged out)
        } catch (e) {
            console.error('Error logging out:', e);
        }
    }, []);

    const renderFormComponent = (formType: Segment['formType']) => {
        switch (formType) {
            case 'digital_consent':
                return (
                    <Box sx={{ my: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={digitalConsent.email}
                                    onChange={(e) => setDigitalConsent({ ...digitalConsent, email: e.target.checked })}
                                />
                            }
                            label="Email"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={digitalConsent.text}
                                    onChange={(e) => setDigitalConsent({ ...digitalConsent, text: e.target.checked })}
                                />
                            }
                            label="Text Message"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={digitalConsent.video_call}
                                    onChange={(e) => setDigitalConsent({ ...digitalConsent, video_call: e.target.checked })}
                                />
                            }
                            label="Zoom/Video Call"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            (check next to each approved method)
                        </Typography>
                    </Box>
                );

            case 'media_release':
                return (
                    <Box sx={{ my: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <RadioGroup
                            value={mediaReleaseSigned ? 'yes' : 'no'}
                            onChange={(e) => setMediaReleaseSigned(e.target.value === 'yes')}
                        >
                            <FormControlLabel
                                value="yes"
                                control={<Radio />}
                                label="I give permission for photos, videos, or recordings from sessions to be used for training, marketing, or educational purposes."
                            />
                            <FormControlLabel
                                value="no"
                                control={<Radio />}
                                label="I do NOT give permission for any media to be used publicly."
                            />
                        </RadioGroup>
                    </Box>
                );

            case 'emergency_contact':
                return (
                    <Box sx={{ my: 2 }}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={4}
                            label="Emergency Contact Information"
                            placeholder="Please provide at least two emergency contacts with names and phone numbers"
                            value={emergencyContact}
                            onChange={(e) => setEmergencyContact(e.target.value)}
                            helperText="Enter emergency contact information (name, phone, relationship, etc.)"
                        />
                    </Box>
                );

            case 'signature':
                return (
                    <Box sx={{ my: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>Child's Name:</strong> {user?.first_name || ''} {user?.last_name || ''}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>Guardian Name:</strong> {user?.first_name || ''} {user?.last_name || ''}
                        </Typography>
                        <TextField
                            fullWidth
                            required
                            label="Type Your Name (Signature)"
                            placeholder="Type your full name to sign"
                            value={signature}
                            onChange={(e) => setSignature(e.target.value)}
                            helperText="By typing your name, you are providing your electronic signature"
                            error={!signature.trim()}
                            sx={{ mb: 2 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            <strong>Date:</strong> {new Date().toLocaleDateString()}
                        </Typography>
                    </Box>
                );

            default:
                return null;
        }
    };

    if (!open) return null;

    if (loading) {
        return (
            <Dialog open={open} aria-labelledby="waiver-title" maxWidth="md" fullWidth>
                <DialogTitle id="waiver-title">Liability Waiver</DialogTitle>
                <DialogContent>
                    <Typography>Loading waiver form...</Typography>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog
            open={open}
            aria-labelledby="waiver-title"
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    maxHeight: '90vh'
                }
            }}
        >
            <DialogTitle id="waiver-title">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Guardian Waiver & Consent Form</Typography>
                    <Button variant="text" color="primary" onClick={onLogout}>Logout</Button>
                </Box>
            </DialogTitle>
            <DialogContent dividers sx={{ overflowY: 'auto' }}>
                <Box
                    sx={{
                        '& p': { margin: 0 },
                        '& p + p': { marginTop: 2 },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                            marginTop: 3,
                            marginBottom: 2,
                            fontWeight: 'bold',
                            color: 'text.primary'
                        },
                        '& ul, & ol': {
                            marginTop: 2,
                            marginBottom: 2,
                            paddingLeft: 3
                        },
                        '& li': {
                            marginBottom: 1
                        },
                        '& hr': {
                            marginTop: 3,
                            marginBottom: 3,
                            borderColor: 'divider'
                        },
                        '& strong': {
                            fontWeight: 'bold'
                        }
                    }}
                >
                    {segments.map((segment, index) => {
                        if (segment.type === 'markdown' && segment.content) {
                            return (
                                <ReactMarkdown key={`markdown-${index}`}>
                                    {segment.content}
                                </ReactMarkdown>
                            );
                        } else if (segment.type === 'form' && segment.formType) {
                            return (
                                <Box key={`form-${index}`}>
                                    {renderFormComponent(segment.formType)}
                                </Box>
                            );
                        }
                        return null;
                    })}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="outlined"
                    color="secondary"
                    onClick={onLogout}
                >
                    Logout
                </Button>
                <Button
                    color="primary"
                    variant="contained"
                    onClick={onAccept}
                    disabled={!signature.trim()}
                >
                    I accept
                </Button>
            </DialogActions>
        </Dialog>
    );
}
